import { Head, Link, router } from '@inertiajs/react'
import { useEffect, useState } from 'react'

import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { UserType } from './types'
import {Button} from "@/components/ui/button.tsx";
import { Clock, MoreVertical, Plus, Search, Shield, UserCheck, UserX} from "lucide-react";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {Table, TableBody, TableCell, TableHead, TableRow} from "@/components/ui/table"
import {TableHeader} from "@/components/ui/table.tsx";
import {Badge} from "@/components/ui/badge.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu.tsx";
import Pagination from '@/components/Pagination'

interface IndexProps {
  users: UserType[]
  pagination: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

export default function Index({ users, pagination }: IndexProps) {

  const [searchTerm, setSearchTerm] = useState(() => {
    if (typeof window === 'undefined') return ''
    return new URLSearchParams(window.location.search).get('q') ?? ''
  })
  const [statusFilter, setStatusFilter] = useState(() => {
    if (typeof window === 'undefined') return 'all'
    return new URLSearchParams(window.location.search).get('status') ?? 'all'
  })
  const [roleFilter, setRoleFilter] = useState(() => {
    if (typeof window === 'undefined') return 'all'
    return new URLSearchParams(window.location.search).get('role') ?? 'all'
  })
  const [page, setPage] = useState(() => {
    if (typeof window === 'undefined') return 1
    return Number(new URLSearchParams(window.location.search).get('page')) || 1
  })
  const perPage = pagination?.per_page || 10

  useEffect(() => {
    const timeout = setTimeout(() => {
      router.get(
        '/admin/users',
        {
          q: searchTerm.trim() || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          role: roleFilter === 'all' ? undefined : roleFilter,
          page,
          per_page: perPage,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['users', 'pagination'],
        }
      )
    }, 300)

    return () => clearTimeout(timeout)
  }, [searchTerm, statusFilter, roleFilter, page, perPage])


  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "super_admin":
        return "default"
      case "admin":
        return "secondary"
      case "manager":
        return "outline"
      default:
        return "outline"
    }
  }

  const getStatusBadgeVariant = (status: string) => {
    return status === "active" ? "default" : "secondary"
  }

  return (
    <>
      <Head title="Users" />
      <div className="space-y-6 p-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">User Management</h1>
            <p className="text-muted-foreground">Manage system users and their permissions</p>
          </div>
          <Link href="/admin/users/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          </Link>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">0</div>
              <p className="text-xs text-muted-foreground">Across all roles</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">0</div>
              <p className="text-xs text-muted-foreground">Currently active</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Inactive Users</CardTitle>
              <UserX className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">0</div>
              <p className="text-xs text-muted-foreground">Deactivated accounts</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Administrators</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">0</div>
              <p className="text-xs text-muted-foreground">Admin level access</p>
            </CardContent>
          </Card>
        </div>


        <Card>
          <CardHeader>
            <CardTitle>System Users</CardTitle>
            <CardDescription>View and manage all system users</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                      setPage(1)
                    }}
                    className="pl-9"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="super_admin">Super Administrator</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Community</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium text-foreground">{user.firstName}</div>
                            <div className="text-sm text-muted-foreground">{user.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getRoleBadgeVariant(user.role!)}>{user.role}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {user.communities?.length
                            ? user.communities.map((community) => community.name).join(', ')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(user.status!)}>
                            {user.status === "active" ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Clock className="h-3 w-3" />
                            {new Date(user.last_sign_in_at!).toLocaleDateString()}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link href={`/admin/users/${user.id}`}>View Details</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link href={`/admin/users/${user.id}/edit`}>Edit User</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link href={`/admin/users/${user.id}/communities`}>Assign Communities</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link href={`/users/${user.id}/permissions`}>Manage Permissions</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem>Reset Password</DropdownMenuItem>
                              <DropdownMenuItem className="text-destructive">
                                {user.status === "active" ? "Deactivate" : "Activate"}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Pagination
              className="mt-4"
              page={pagination.page}
              totalPages={pagination.total_pages}
              total={pagination.total}
              onPageChange={(nextPage) => setPage(nextPage)}
            />
          </CardContent>
        </Card>

        {/*<div className="mt-6 grid gap-4 md:grid-cols-[1fr_200px_200px]">*/}
        {/*  <div>*/}
        {/*    <Label htmlFor="user-search">Search</Label>*/}
        {/*    <Input*/}
        {/*      id="user-search"*/}
        {/*      name="user-search"*/}
        {/*      value={searchTerm}*/}
        {/*      placeholder="Search by first or last name"*/}
        {/*      onChange={(event) => setSearchTerm(event.target.value)}*/}
        {/*    />*/}
        {/*  </div>*/}
        {/*  <div>*/}
        {/*    <Label htmlFor="user-status">Status</Label>*/}
        {/*    <Select value={statusFilter} onValueChange={setStatusFilter}>*/}
        {/*      <SelectTrigger id="user-status">*/}
        {/*        <SelectValue placeholder="All statuses" />*/}
        {/*      </SelectTrigger>*/}
        {/*      <SelectContent>*/}
        {/*        <SelectItem value="all">All</SelectItem>*/}
        {/*        <SelectItem value="active">Active</SelectItem>*/}
        {/*        <SelectItem value="inactive">Inactive</SelectItem>*/}
        {/*      </SelectContent>*/}
        {/*    </Select>*/}
        {/*  </div>*/}
        {/*  <div>*/}
        {/*    <Label htmlFor="user-role">Role</Label>*/}
        {/*    <Select value={roleFilter} onValueChange={setRoleFilter}>*/}
        {/*      <SelectTrigger id="user-role">*/}
        {/*        <SelectValue placeholder="All roles" />*/}
        {/*      </SelectTrigger>*/}
        {/*      <SelectContent>*/}
        {/*        <SelectItem value="all">All</SelectItem>*/}
        {/*        <SelectItem value="agent">Agent</SelectItem>*/}
        {/*        <SelectItem value="admin">Admin</SelectItem>*/}
        {/*        <SelectItem value="super_admin">Super admin</SelectItem>*/}
        {/*        <SelectItem value="manager">Manager</SelectItem>*/}
        {/*      </SelectContent>*/}
        {/*    </Select>*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/*<div className="min-w-full mt-6 overflow-x-auto">*/}
        {/*  <table className="w-full text-left border-collapse">*/}
        {/*    <thead>*/}
        {/*      <tr className="border-b">*/}
        {/*        <th className="py-3 pr-4 font-semibold">Code</th>*/}
        {/*        <th className="py-3 pr-4 font-semibold">Name</th>*/}
        {/*        <th className="py-3 pr-4 font-semibold">Email</th>*/}
        {/*        <th className="py-3 pr-4 font-semibold">Role</th>*/}
        {/*        <th className="py-3 pr-4 font-semibold">Status</th>*/}
        {/*        <th className="py-3 pr-4 font-semibold">Actions</th>*/}
        {/*      </tr>*/}
        {/*    </thead>*/}
        {/*    <tbody>*/}
        {/*      {users.map((user) => (*/}
        {/*        <tr key={user.id} className="border-b last:border-b-0">*/}
        {/*          <td className="py-3 pr-4">{user.code || '-'}</td>*/}
        {/*          <td className="py-3 pr-4">*/}
        {/*            {[user.firstName, user.lastName].filter(Boolean).join(' ') ||*/}
        {/*              '-'}*/}
        {/*          </td>*/}
        {/*          <td className="py-3 pr-4">{user.email}</td>*/}
        {/*          <td className="py-3 pr-4">{user.role}</td>*/}
        {/*          <td className="py-3 pr-4">{user.status}</td>*/}
        {/*          <td className="py-3 pr-4">*/}
        {/*            <Link*/}
        {/*              href={`/admin/users/${user.id}/edit`}*/}
        {/*              className="rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-accent"*/}
        {/*            >*/}
        {/*              Edit*/}
        {/*            </Link>*/}
        {/*          </td>*/}
        {/*        </tr>*/}
        {/*      ))}*/}
        {/*      {users.length === 0 && (*/}
        {/*        <tr>*/}
        {/*          <td className="py-6 text-sm text-muted-foreground" colSpan={6}>*/}
        {/*            No users found.*/}
        {/*          </td>*/}
        {/*        </tr>*/}
        {/*      )}*/}
        {/*    </tbody>*/}
        {/*  </table>*/}
        {/*</div>*/}
      </div>
    </>
  )
}
